import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { ConversationService } from '../../../../core/src/lib/conversation.service';
import { ConversationDetail } from '../../../../core/src/lib/interface/conversation.interface';
import { Observable, ReplaySubject } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { ConversationChannel } from '@vendasta/conversation';
import { PARTNER_ID_TOKEN } from './../../../../core/src/lib/tokens';
import { FeatureFlagService } from '@galaxy/partner';
import { INBOX_PREVIEW_CHANNEL_ICON } from './../../../../core/src/lib/inbox.constants';

@Component({
  selector: 'inbox-conversation-preview',
  templateUrl: './inbox-conversation-preview.component.html',
  styleUrls: ['./inbox-conversation-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InboxConversationPreviewComponent {
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly conversationService = inject(ConversationService);
  private readonly featureFlagService = inject(FeatureFlagService);

  private readonly conversationDetail$$ = new ReplaySubject<ConversationDetail>(1);
  private readonly conversationDetail$ = this.conversationDetail$$.asObservable();
  protected readonly previewContent$ = this.conversationDetail$.pipe(
    switchMap((details) => this.conversationService.getPreviewContent(details)),
  );

  readonly showChannelIcon$: Observable<boolean> = this.partnerId$.pipe(
    switchMap((partnerId) => {
      return this.featureFlagService.batchGetStatus(partnerId, '', [INBOX_PREVIEW_CHANNEL_ICON]);
    }),
    filter((ff) => !!ff),
    map((feature) => feature[INBOX_PREVIEW_CHANNEL_ICON]),
    shareReplay(1),
  );

  readonly latestMessageIcon$ = this.conversationDetail$.pipe(
    map((details) => this.getLatestMessageIcon(details.latestMessage.channel)),
  );

  @Input() set conversationDetail(value: ConversationDetail) {
    this.conversationDetail$$.next(value);
  }

  private getLatestMessageIcon(channel: ConversationChannel): string {
    switch (channel) {
      case ConversationChannel.CONVERSATION_CHANNEL_SMS:
        return 'phone';
      case ConversationChannel.CONVERSATION_CHANNEL_EMAIL:
        return 'email';
      case ConversationChannel.CONVERSATION_CHANNEL_FACEBOOK:
        return 'facebook';
      case ConversationChannel.CONVERSATION_CHANNEL_GOOGLE_BUSINESS_COMMUNICATIONS ||
        ConversationChannel.CONVERSATION_CHANNEL_GOOGLE_MESSAGES:
        return 'google';
      default:
        return 'internal';
    }
  }
}
